// Read-only line-items sub-table (app.md §8). Editable line items live in the
// Import Control detail modal, not here. Flat style (v1.20): no panel fill —
// the queue card / modal section provides the container — with small
// uppercase column headers. The footer is a pass/fail "Reconciles" /
// "Doesn't reconcile" chip beside the invoice's Total (item 7, 2026-08-13,
// which dropped the spelled-out arithmetic — line items = subtotal, since the
// lines are pre-tax, and subtotal + tax = total — that used to sit beside the
// chip and repeated figures already on screen). The check behind the chip is
// unchanged: the same one that gates release at step 5.
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Body1,
  Button,
  Caption1,
  Checkbox,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import type { AssociatedEntity, InvoiceLineItem } from '../lib/types'
import { checkAmounts, formatCurrency, toNumber } from '../lib/format'
import { TINT_PILLS } from '../lib/theme'
import { DotPill } from './Badges'
import { EntityPicker, type EntityOption } from './EntityPicker'
import {
  loadBulkCostCenterOptions,
  searchAccounts,
  searchCostCenters,
  searchExpenseTypes,
} from '../lib/data'
import { lineItemCodingLabel } from '../lib/format'
import { useHalfLabels } from '../lib/hooks'

const useStyles = makeStyles({
  panel: { padding: '2px 0' },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // Horizontal scroller around the table only (the reconcile footer below wraps
  // on its own). Keeps a narrow modal from being widened by the table — at
  // ~900px the columns used to be squeezed until Description was unreadable and
  // the header labels drew over each other (QA Q17).
  tableScroll: { overflowX: 'auto', maxWidth: '100%', minWidth: 0 },
  // Fixed layout so the unsized data columns split the width evenly. minWidth is
  // the sized columns (330px) plus a floor for Description + Account; below it
  // the wrapper scrolls rather than crushing the text columns. Header labels
  // clip with an ellipsis instead of spilling into the next column.
  table: {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: '660px',
    fontVariantNumeric: 'tabular-nums',
    '& .fui-TableHeaderCell__button': {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  // A third text column (Company, on a cross-company split) needs its own floor.
  tableWithCompany: { minWidth: '820px' },
  head: {
    '& .fui-TableHeaderCell__button': {
      fontSize: '10.5px',
      fontWeight: tokens.fontWeightSemibold,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: tokens.colorNeutralForeground3,
    },
  },
  total: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '10px',
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
  },
  num: { textAlign: 'right' },
  numHeader: {
    textAlign: 'right',
    '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end', width: '100%' },
  },
  // Tagged-entity chips under a line's description (AE4 read-only display).
  entityRow: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '3px' },
  entityChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 7px',
    borderRadius: tokens.borderRadiusCircular,
    fontSize: '10.5px',
    lineHeight: '1.5',
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
  },
  // Unmatched tags read dimmer — they still need a human to resolve them.
  entityChipUnmatched: { color: tokens.colorNeutralForeground4, fontStyle: 'italic' },
  // Fluent's own TableCell class (size="small") sets `display: table-cell` AND
  // `height: 34px`. On a real `<td>`, `height` behaves as a MINIMUM, so a
  // taller picker stack grows the row for free. This class used to also set
  // `display: flex` — same property Fluent's class sets, so ours won the
  // merge (it's appended last) — which switched the cell to flex layout
  // WITHOUT clearing Fluent's `height: 34px`, and on a flex item `height` is
  // enforced rather than treated as a floor: a 51px picker stack got clipped
  // to 34px instead of growing the row. Nothing here sets `display` any more —
  // the flex row moves to `accountCellInner` below, an ordinary block child,
  // so the `<td>`'s own table-cell sizing is what governs the row.
  accountCell: { minWidth: 0 },
  // The picker (or the read-only label) sits alone in the cell now that the
  // per-line "how was this coded" provenance button is gone. Restoring
  // `display: table-cell` on the cell above also restores Fluent's
  // `vertical-align: middle`, which the OTHER cells in the row inherit from
  // their own (unrelated) TableCell class too — the account cell is the tall
  // one, so centering does nothing to IT. `alignItems: 'flex-start'` keeps this
  // cell's own content top-aligned with the rest of the row.
  accountCellInner: { display: 'flex', alignItems: 'flex-start', gap: '2px', minWidth: 0 },
  // Two or three pickers in one cell stack; one picker looks exactly as it did.
  pickerStack: { display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0, flexGrow: 1 },
  // §9.3 — the bulk apply bar, above the table it acts on.
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    padding: '6px 0 8px',
    minWidth: 0,
  },
  bulkCount: { color: tokens.colorNeutralForeground2, whiteSpace: 'nowrap' },
  bulkPicker: { minWidth: '220px', flexGrow: 1, maxWidth: '340px' },
  // The empty intersection is a real answer, not a failure: say so and point at
  // per-line editing rather than rendering a dropdown with nothing in it.
  bulkNone: { color: tokens.colorNeutralForeground3 },
  bulkError: { color: tokens.colorPaletteRedForeground1 },
  selectCell: { width: '36px' },
})

/** Compact "type: name-or-sourcetext" label for a tagged entity. */
function entityLabel(e: AssociatedEntity): string {
  const type = e.entity_type ?? 'Entity'
  const detail = e.entity_name?.trim() || e.entity_source_text?.trim() || '(unresolved)'
  return `${type}: ${detail}`
}

function lineItemsTotal(lineItems: InvoiceLineItem[]): number {
  return lineItems.reduce((sum, c) => sum + toNumber(c.total), 0)
}

interface Props {
  lineItems: InvoiceLineItem[] | 'loading' | undefined
  /** Invoice amounts for the reconciliation footer. */
  subtotal?: number | string | null
  tax?: number | string | null
  total?: number | string | null
  /** The invoice's company id — when a line's company differs (a cross-company
      split set at Import Control), a Company column appears so approvers see
      exactly which lines route elsewhere. */
  invoiceCompanyId?: string | null
  /** Optional per-line-item tagged entities (AE4), keyed by id. When
      supplied, each line lists its entities under the description; absent = the
      feature renders nothing (backward compatible). */
  entitiesByLineItem?: Record<string, AssociatedEntity[]>
  /** When provided, the Account column offers a "What is it?" picker — the
      expense-type half-write. The three approval tiers hold this action as well
      as the cost-center one; the server's matrix has always granted it and the
      route has always existed, so a tier that renders only "Who is it for?" is
      offering half of what its people may do. */
  onExpenseTypeChange?: (lineItem: InvoiceLineItem, expenseTypeCode: string) => void
  /** When provided, the Account column offers a "Who is it for?" picker —
      the cost-center half-write, which is what the three approval tiers hold.
      Options derive from the line's own expense type (one-directional). */
  onCostCenterChange?: (lineItem: InvoiceLineItem, costCenterCode: string) => void
  /** When provided, the Account column becomes an editable GL-account picker
      (planning §3 — GL editable at every approval step + Export Control). The
      parent asks the API to apply it; the API checks the caller's authority for
      the step, stamps the account as manually set and writes the correction row.
      Absent = read-only text (the default). */
  onAccountChange?: (lineItem: InvoiceLineItem, accountId: string) => void
  /** §9.3 — BULK "Who is it for?". When provided, the table grows a select
      column and an apply bar over the lines that have no cost center yet.

      It is passed exactly where `onCostCenterChange` is: the same five steps
      hold `edit_line_item_cost_center`. It exists because approval is refused
      while any line lacks a cost center and almost no invoice arrives coded on
      that half, so the alternative is twelve pickers and twelve saves.

      Deliberately NOT paired with an "accept all suggestions": the model never
      proposes a cost center, so there are no suggestions to accept. */
  onBulkCostCenter?: (lineItems: InvoiceLineItem[], costCenterCode: string) => Promise<void> | void
  /** Line item id whose account write is in flight — disables that row's picker. */
  savingLineItemId?: string
}

export function InvoiceLineItemsTable({ lineItems, subtotal, tax, total, invoiceCompanyId, entitiesByLineItem, onAccountChange, onExpenseTypeChange, onCostCenterChange, onBulkCostCenter, savingLineItemId }: Props) {
  const s = useStyles()
  // Codes are what a line carries; the labels are master data. Resolved once per
  // session — see `useHalfLabels`.
  const labels = useHalfLabels()

  // --- §9.3, bulk apply -----------------------------------------------------
  //
  // EVERY HOOK BELOW RUNS BEFORE THE EARLY RETURNS. The loading and empty arms
  // return before the table renders, so a hook placed after them would change
  // the hook order between renders.
  const rows = useMemo(
    () => (Array.isArray(lineItems) ? lineItems : []),
    [lineItems],
  )
  // Eligible = lines with no cost center. Select-all covers exactly these, and
  // the server refuses a line that already has one, so bulk apply can never
  // overwrite an answer somebody gave.
  const eligible = useMemo(() => rows.filter((c) => !c.cost_center_code), [rows])
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([])
  const eligibleKey = eligible.map((c) => c.id).join('|')
  useEffect(() => {
    // A reload may have coded, removed or added lines. Drop anything no longer
    // eligible rather than sending the server a stale selection.
    const live = new Set(eligibleKey.split('|'))
    setSelectedIds((prev) => prev.filter((id) => live.has(id)))
  }, [eligibleKey])

  const selected = useMemo(
    () => eligible.filter((c) => selectedIds.includes(c.id)),
    [eligible, selectedIds],
  )
  // The identity of the selection AS IT AFFECTS THE OPTION LIST — the ids and
  // the expense types, since those are the only inputs to the intersection.
  const selectionKey = selected.map((c) => `${c.id}:${c.expense_type_code ?? ''}`).join('|')

  const [options, setOptions] = useState<EntityOption[] | 'loading'>([])
  const [pick, setPick] = useState<EntityOption | undefined>(undefined)
  const [applying, setApplying] = useState(false)
  const [bulkError, setBulkError] = useState<string | null>(null)

  useEffect(() => {
    if (selectionKey === '') {
      setOptions([])
      return
    }
    let live = true
    setOptions('loading')
    // THE INTERSECTION of `costCentersFor` across the selection — one cost
    // center has to compose with every selected line. An empty result is a real
    // answer; the bar says so instead of offering an empty dropdown.
    loadBulkCostCenterOptions(selectionKey.split('|').map((part) => part.split(':')[1] ?? ''))
      .then((list) => {
        if (!live) return
        setOptions(list.map((c) => ({ id: c.cost_center_code, primary: c.label, secondary: c.cost_center_code })))
      })
      .catch(() => { if (live) setOptions([]) })
    return () => { live = false }
  }, [selectionKey])

  // A pick that survived a selection change must not be applied blind: it may
  // no longer compose with everything selected.
  useEffect(() => {
    if (options === 'loading') return
    setPick((prev) => (prev && options.some((o) => o.id === prev.id) ? prev : undefined))
  }, [options])

  const searchOptions = useCallback(
    (term: string): Promise<EntityOption[]> => {
      const list = options === 'loading' ? [] : options
      const q = term.trim().toLowerCase()
      return Promise.resolve(
        q === '' ? list : list.filter((o) => `${o.primary} ${o.secondary ?? ''}`.toLowerCase().includes(q)),
      )
    },
    [options],
  )

  const applyBulk = useCallback(async () => {
    if (!onBulkCostCenter || !pick || selected.length === 0) return
    setBulkError(null)
    setApplying(true)
    try {
      await onBulkCostCenter(selected, pick.id)
      // The parent reloads the lines; the selection prunes itself to whatever
      // is still eligible on the next render.
      setPick(undefined)
    } catch (e) {
      setBulkError(e instanceof Error ? e.message : String(e))
    } finally {
      setApplying(false)
    }
  }, [onBulkCostCenter, pick, selected])

  if (lineItems === undefined || lineItems === 'loading') {
    return <div className={s.panel}><Spinner size="tiny" label="Loading line items…" /></div>
  }
  if (lineItems.length === 0) {
    return <div className={s.panel}><Body1 className={s.none}>No line items on this invoice.</Body1></div>
  }
  // Hidden in the common case (every line on the invoice's company) — only a
  // genuine split earns the extra column.
  const showCompany = lineItems.some((c) => c.company_id && c.company_id !== (invoiceCompanyId ?? undefined))
  // The select column earns its place only where there is something to select:
  // a fully coded invoice renders exactly as it did before.
  const bulk = onBulkCostCenter !== undefined && eligible.length > 0
  const allSelected = bulk && selected.length === eligible.length
  const optionList = options === 'loading' ? [] : options
  // Which coding columns this surface gets. Derived from the callbacks it was
  // handed, exactly as the controls always were — nothing here needs to know
  // which screen it is on.
  const editsHalves = onExpenseTypeChange !== undefined || onCostCenterChange !== undefined
  // No callback at all = a read-only surface, which shows the settled account.
  const readOnlyCoding = onAccountChange === undefined && !editsHalves
  const showAccount = onAccountChange !== undefined || readOnlyCoding
  // "Who is it for?" gets its OWN column gate, separate from `editsHalves`
  // (issue 3, 2026-08-11). AP/AR holds "What is it?" and nothing else — no
  // Account picker, no cost center at all, not even read-only — so a surface
  // passing only `onExpenseTypeChange` must not grow a "Who is it for?" column
  // it has no callback for. Every existing caller that granted a cost center
  // half already passed `onCostCenterChange` (Export Control, Manual Entry, the
  // three approval tiers), so this narrows nothing for them.
  const showWhoIsItFor = onCostCenterChange !== undefined
  return (
    <div className={s.panel}>
      {bulk && (
        <div className={s.bulkBar}>
          <Caption1 className={s.bulkCount}>
            {selected.length === 0
              ? `${eligible.length} line${eligible.length === 1 ? '' : 's'} still need “Who is it for?”`
              : `${selected.length} selected`}
          </Caption1>
          {selected.length > 0 && (
            options === 'loading' ? (
              <Spinner size="tiny" label="Finding what they have in common…" />
            ) : optionList.length === 0 ? (
              // THE EMPTY INTERSECTION. 91% of the possible cost-center ×
              // expense-type combinations are not accounts, so a mixed
              // selection often has nothing in common — and a line with no
              // expense type at all composes with nothing.
              <Caption1 className={s.bulkNone}>
                These lines have no cost center in common, so one answer cannot cover them. Set
                “Who is it for?” on each line instead, or narrow the selection.
              </Caption1>
            ) : (
              <>
                {/* The picker's own label (below) now names the question — no
                    separate caption needed beside it, and one would only
                    repeat the same three words a line above its own label. */}
                <div className={s.bulkPicker}>
                  <EntityPicker
                    size="small"
                    label="Who is it for?"
                    value={{ id: pick?.id, name: pick?.primary }}
                    disabled={applying}
                    search={searchOptions}
                    onSelect={setPick}
                  />
                </div>
                <Button
                  size="small"
                  appearance="primary"
                  disabled={!pick || applying}
                  onClick={() => { void applyBulk() }}
                >
                  {applying ? 'Applying…' : 'Apply'}
                </Button>
              </>
            )
          )}
          {bulkError && <Caption1 className={s.bulkError}>{bulkError}</Caption1>}
        </div>
      )}
      <div className={s.tableScroll}>
      <Table size="small" aria-label="Line items" className={mergeClasses(s.table, showCompany && s.tableWithCompany)}>
        <TableHeader>
          <TableRow>
            {bulk && (
              <TableHeaderCell className={mergeClasses(s.head, s.selectCell)}>
                <Checkbox
                  aria-label="Select every line that still needs a cost center"
                  title="Select every line that still needs a cost center"
                  checked={allSelected ? true : selected.length > 0 ? 'mixed' : false}
                  disabled={applying}
                  onChange={(_e, d) =>
                    setSelectedIds(d.checked === true ? eligible.map((c) => c.id) : [])
                  }
                />
              </TableHeaderCell>
            )}
            <TableHeaderCell style={{ width: 40 }} className={s.head}>#</TableHeaderCell>
            {/* Company leads the data columns after # (planning §4); still
                conditional — it appears only on a genuine cross-company split. */}
            {showCompany && <TableHeaderCell className={s.head}>Company</TableHeaderCell>}
            <TableHeaderCell className={s.head}>Description</TableHeaderCell>
            {/* "Account" NAMES A WHOLE ACCOUNT, so it appears only where one is
                actually settled or displayed as settled: Export Control and
                Manual Entry, which hold the composed write, and the read-only
                surfaces (Closing Confirmation, Denial Review, Archives). The
                three approval tiers settle the two HALVES and never the whole,
                so they get the two questions as their own columns instead — one
                column holding a stack of two labelled pickers read as a single
                broken Account field (owner, 2026-08-10). */}
            {showAccount && <TableHeaderCell className={s.head}>Account</TableHeaderCell>}
            {editsHalves && <TableHeaderCell className={s.head}>What is it?</TableHeaderCell>}
            {showWhoIsItFor && <TableHeaderCell className={s.head}>Who is it for?</TableHeaderCell>}
            {/* Compact fixed widths so Description/coding absorb the slack —
                no dead band before the right-aligned numbers. */}
            <TableHeaderCell style={{ width: 100 }} className={mergeClasses(s.head, s.numHeader)}>Price</TableHeaderCell>
            <TableHeaderCell style={{ width: 90 }} className={mergeClasses(s.head, s.numHeader)}>Quantity</TableHeaderCell>
            <TableHeaderCell style={{ width: 100 }} className={mergeClasses(s.head, s.numHeader)}>Total</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lineItems.map((c) => {
            const entities = entitiesByLineItem?.[c.id]
            const selectable = bulk && !c.cost_center_code
            return (
            <TableRow key={c.id}>
              {bulk && (
                <TableCell className={s.selectCell}>
                  {selectable && (
                    <Checkbox
                      aria-label={`Select line ${c.line_number ?? ''}`}
                      checked={selectedIds.includes(c.id)}
                      disabled={applying}
                      onChange={(_e, d) =>
                        setSelectedIds((prev) =>
                          d.checked === true
                            ? [...prev.filter((id) => id !== c.id), c.id]
                            : prev.filter((id) => id !== c.id),
                        )
                      }
                    />
                  )}
                </TableCell>
              )}
              <TableCell>{c.line_number ?? '—'}</TableCell>
              {showCompany && <TableCell>{c.company_name ?? '—'}</TableCell>}
              <TableCell>
                <span>{c.description ?? '—'}</span>
                {entities && entities.length > 0 && (
                  <div className={s.entityRow}>
                    {entities.map((e) => {
                      const unmatched = !e.match_source || e.match_source === 'Unmatched'
                      return (
                        <span
                          key={e.id}
                          className={mergeClasses(s.entityChip, unmatched && s.entityChipUnmatched)}
                          title={unmatched ? 'Unmatched — needs resolution' : `Match source: ${e.match_source}`}
                        >
                          {entityLabel(e)}
                        </span>
                      )
                    })}
                  </div>
                )}
              </TableCell>
              {/* THE ACCOUNT COLUMN — only where a whole account is settled or
                  shown as settled. The value-or-picker lives in the inner div,
                  not on the TableCell itself: see `accountCell`'s comment
                  above for why. */}
              {showAccount && (
              <TableCell className={s.accountCell}>
                <div className={s.accountCellInner}>
                {readOnlyCoding ? (
                  // BOTH HALVES, always. `account_name` is null while a line is
                  // half-coded, so showing it alone rendered a line that IS
                  // coded — it just has no department yet — identically to one
                  // nobody has touched, at exactly the steps that have to tell
                  // them apart.
                  lineItemCodingLabel(c, labels.expenseType(c.expense_type_code))
                ) : (
                  <div className={s.pickerStack}>
                    {onAccountChange && (
                      <EntityPicker
                        size="small"
                        ariaLabel="Account"
                        value={{ id: c.final_account_id ?? undefined, name: c.account_name ?? undefined }}
                        placeholder="Search GL accounts…"
                        disabled={savingLineItemId === c.id}
                        search={(t) =>
                          searchAccounts(t).then<EntityOption[]>((as) =>
                            as.map((a) => ({
                              id: a.account_id ?? '',
                              primary: a.account_name ?? '(unnamed)',
                              secondary: a.account_id,
                            })),
                          )
                        }
                        onSelect={(o) => onAccountChange(c, o.id)}
                      />
                    )}
                  </div>
                )}
                </div>
              </TableCell>
              )}
              {/* "WHAT IS IT?" — its own column, named by the header rather than
                  by a label inside every cell. Read-only where the surface holds
                  the other half-write but not this one. */}
              {editsHalves && (
              <TableCell className={s.accountCell}>
                <div className={s.accountCellInner}>
                  <div className={s.pickerStack}>
                    {onExpenseTypeChange ? (
                      <EntityPicker
                        size="small"
                        ariaLabel="What is it?"
                        value={{
                          id: c.expense_type_code ?? undefined,
                          name: labels.expenseType(c.expense_type_code),
                        }}
                        // The column header asks the question, so the default
                        // "Search…" placeholder says what to DO rather than
                        // restating which field this is.
                        disabled={savingLineItemId === c.id}
                        search={(t) =>
                          searchExpenseTypes(t).then<EntityOption[]>((es) =>
                            es.map((e) => ({
                              id: e.expense_type_code,
                              primary: e.label,
                              secondary: e.expense_type_code,
                            })),
                          )
                        }
                        onSelect={(o) => onExpenseTypeChange(c, o.id)}
                      />
                    ) : (
                      <Caption1>{labels.expenseType(c.expense_type_code) ?? '—'}</Caption1>
                    )}
                  </div>
                </div>
              </TableCell>
              )}
              {/* "WHO IS IT FOR?" — likewise its own column, but gated on
                  `showWhoIsItFor`, not `editsHalves`: AP/AR grants "What is
                  it?" alone (issue 3, 2026-08-11), and this column has nothing
                  to show — not even read-only — where there is no cost-center
                  grant at all. */}
              {showWhoIsItFor && (
              <TableCell className={s.accountCell}>
                <div className={s.accountCellInner}>
                  <div className={s.pickerStack}>
                    {onCostCenterChange ? (
                      <EntityPicker
                        size="small"
                        ariaLabel="Who is it for?"
                        value={{
                          id: c.cost_center_code ?? undefined,
                          name: labels.costCenter(c.cost_center_code),
                        }}
                        // The column header names the question, so the
                        // placeholder's job when EMPTY is to say why the box is
                        // greyed out — a disabled control with no explanation
                        // reads as broken rather than gated. Omit it (falls to
                        // EntityPicker's own "Search…" default) once there is
                        // nothing gated left to explain.
                        placeholder={!c.expense_type_code ? 'Set “What is it?” first' : undefined}
                        disabled={savingLineItemId === c.id || !c.expense_type_code}
                        // What this picker OFFERS depends on the expense type, and
                        // the row's key is `c.id`, so changing "What is it?" reuses
                        // this same picker instance. Naming the dependency is the
                        // only signal EntityPicker can act on: `search` is a fresh
                        // closure every render, so its identity says nothing.
                        // Without this the cleared field reopens showing the PREVIOUS
                        // expense type's cost centres until the reviewer types.
                        optionsKey={c.expense_type_code ?? undefined}
                        search={(t) =>
                          searchCostCenters(t, c.expense_type_code ?? undefined).then<EntityOption[]>((cs) =>
                            cs.map((cc) => ({
                              id: cc.cost_center_code,
                              primary: cc.label,
                              secondary: cc.cost_center_code,
                            })),
                          )
                        }
                        onSelect={(o) => onCostCenterChange(c, o.id)}
                      />
                    ) : (
                      <Caption1>{labels.costCenter(c.cost_center_code) ?? '—'}</Caption1>
                    )}
                  </div>
                </div>
              </TableCell>
              )}
              <TableCell className={s.num}>{formatCurrency(c.unit_price)}</TableCell>
              <TableCell className={s.num}>{c.quantity ?? '—'}</TableCell>
              <TableCell className={s.num}>{formatCurrency(c.total)}</TableCell>
            </TableRow>
            )
          })}
        </TableBody>
      </Table>
      </div>
      <ReconcileFooter lineItemsSum={lineItemsTotal(lineItems)} subtotal={subtotal} tax={tax} total={total} />
    </div>
  )
}

function ReconcileFooter({
  lineItemsSum,
  subtotal,
  tax,
  total,
}: {
  lineItemsSum: number
  subtotal?: number | string | null
  tax?: number | string | null
  total?: number | string | null
}) {
  const s = useStyles()
  // Without invoice amounts there is nothing to reconcile against.
  if (subtotal == null && tax == null && total == null) {
    return <div className={s.total}>Line items total: {formatCurrency(lineItemsSum)}</div>
  }
  // Line items are pre-tax, so they are read against the SUBTOTAL; the tax is
  // spread across GL codes only when the Point Click Care CSV is built
  // (`docs/export.md` → Sales tax), never before (lib/format.ts checkAmounts).
  // The arithmetic behind `reconciles` is unchanged — only the rendered
  // sentence is gone (item 7, 2026-08-13): it used to spell out
  // "$X in Line Items = $X Subtotal; $X Subtotal + $X Tax = $X Total", which
  // repeated numbers already on screen in the header fields above. The pill
  // still carries the pass/fail read at a glance; the figure beside it is now
  // just the invoice's own Total.
  const { reconciles } = checkAmounts(lineItemsSum, subtotal, tax, total)
  return (
    <div className={s.total}>
      <DotPill pill={reconciles ? TINT_PILLS.green : TINT_PILLS.red}>
        {reconciles ? 'Reconciles' : "Doesn't reconcile"}
      </DotPill>
      <span>{formatCurrency(total)}</span>
    </div>
  )
}
